import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  FormControlLabel,
  Switch,
  Divider,
  alpha,
  Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  Assignment as AssignmentIcon,
  Save as SaveIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

const MotivoFormDialog = ({
  isOpen,
  onClose,
  isEditing,
  currentMotivo,
  formErrors,
  submitting,
  handleInputChange,
  handleSubmit
}) => {
  const theme = useTheme();

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: theme.palette.mode === 'dark'
            ? `linear-gradient(135deg, ${alpha(theme.palette.grey[900], 0.95)} 0%, ${alpha(theme.palette.grey[800], 0.9)} 100%)`
            : 'background.paper',
          backdropFilter: 'blur(10px)',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
          background: theme.palette.mode === 'dark'
            ? `linear-gradient(90deg, ${alpha(theme.palette.primary.dark, 0.3)} 0%, ${alpha(theme.palette.primary.main, 0.2)} 100%)`
            : `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssignmentIcon 
            sx={{ 
              color: theme.palette.primary.main,
              fontSize: 28
            }} 
          />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {isEditing ? 'Editar Motivo' : 'Novo Motivo'}
          </Typography>
        </Box>
        
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: alpha(theme.palette.error.main, 0.1),
              color: theme.palette.error.main,
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Informação sobre o formulário */}
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              background: alpha(theme.palette.info.main, 0.08),
              border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <InfoIcon sx={{ color: theme.palette.info.main, fontSize: 20 }} />
            <Typography variant="body2" color="text.secondary">
              Configure os motivos pelos quais um usuário pode emitir senhas diretamente no sistema.
            </Typography>
          </Box>

          {/* Campo Nome */}
          <TextField
            name="nome"
            label="Nome do Motivo"
            value={currentMotivo.nome}
            onChange={handleInputChange}
            error={!!formErrors.nome}
            helperText={formErrors.nome}
            fullWidth
            required
            placeholder="Ex: Cliente sem senha e fila zerada"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                  borderWidth: 2,
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: theme.palette.primary.main,
              },
            }}
          />

          {/* Campo Descrição */}
          <TextField
            name="descricao"
            label="Descrição (Opcional)"
            value={currentMotivo.descricao}
            onChange={handleInputChange}
            error={!!formErrors.descricao}
            helperText={formErrors.descricao || 'Descrição detalhada do motivo para emissão de senha'}
            fullWidth
            multiline
            rows={3}
            placeholder="Descreva quando este motivo deve ser utilizado..."
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                  borderWidth: 2,
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: theme.palette.primary.main,
              },
            }}
          />

          <Divider sx={{ my: 1 }} />

          {/* Configurações */}
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                mb: 2,
                color: 'text.primary'
              }}
            >
              Configurações
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Switch para Requer Justificativa */}
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                  background: alpha(theme.palette.background.paper, 0.5)
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      name="requer_justificativa"
                      checked={currentMotivo.requer_justificativa}
                      onChange={handleInputChange}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        Requer Justificativa
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Quando ativado, o usuário precisará fornecer uma justificativa adicional
                      </Typography>
                    </Box>
                  }
                  sx={{ m: 0, alignItems: 'flex-start' }}
                />
                {currentMotivo.requer_justificativa && (
                  <Chip
                    label="Justificativa obrigatória"
                    size="small"
                    color="warning"
                    sx={{ mt: 1, ml: 4 }}
                  />
                )}
              </Box>

              {/* Switch para Status Ativo */}
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                  background: alpha(theme.palette.background.paper, 0.5)
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      name="ativo"
                      checked={currentMotivo.ativo}
                      onChange={handleInputChange}
                      color="success"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        Motivo Ativo
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Apenas motivos ativos aparecem na lista de emissão de senhas
                      </Typography>
                    </Box>
                  }
                  sx={{ m: 0, alignItems: 'flex-start' }}
                />
                <Chip
                  label={currentMotivo.ativo ? "Disponível para uso" : "Indisponível"}
                  size="small"
                  color={currentMotivo.ativo ? "success" : "error"}
                  sx={{ mt: 1, ml: 4 }}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          pt: 2,
          gap: 1,
          background: alpha(theme.palette.background.paper, 0.5),
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: 2,
            px: 3,
            borderColor: alpha(theme.palette.text.secondary, 0.3),
            color: 'text.secondary',
            '&:hover': {
              borderColor: theme.palette.text.secondary,
              backgroundColor: alpha(theme.palette.text.secondary, 0.08),
            },
          }}
        >
          Cancelar
        </Button>
        
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting}
          startIcon={<SaveIcon />}
          sx={{
            borderRadius: 2,
            px: 3,
            background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 90%)`,
            '&:hover': {
              background: `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.primary.main} 90%)`,
              transform: 'translateY(-1px)',
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`,
            },
            '&:disabled': {
              background: alpha(theme.palette.action.disabled, 0.3),
            },
            transition: 'all 0.2s ease',
          }}
        >
          {submitting ? 'Salvando...' : (isEditing ? 'Atualizar' : 'Criar Motivo')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MotivoFormDialog; 